/**
 * 🔗 Search.app S3 Redirect Configuration Component
 * Für Segment-Config Modal
 */

import React, { useState, useEffect } from 'react';
import { Link, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const DEFAULT_HYBRID_REGION = 'ireland';
const EXCLUDED_HYBRID_REGIONS = new Set(['frankfurt', 'oregon']);

const SearchAppRedirectConfig = ({ segment, onUpdate, isAdmin }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [useAllDomains, setUseAllDomains] = useState(true);
  const [useCustomDomains, setUseCustomDomains] = useState(false);
  const [customDomainsText, setCustomDomainsText] = useState('');
  const [linksPerMode, setLinksPerMode] = useState('per_segment'); // 'per_segment' or 'per_slot'
  const [useHybridMode, setUseHybridMode] = useState(false); // search.app → S3
  const [hybridRegion, setHybridRegion] = useState(DEFAULT_HYBRID_REGION);
  const [linkStyle, setLinkStyle] = useState('SHORT'); // 'SHORT' or 'UNGUESSABLE'
  const [enabledLinkTypes, setEnabledLinkTypes] = useState({
    logo: true,
    cta: true,
    unsubscribe: true
  });
  
  // Lade Slots beim Mount
  useEffect(() => {
    if (isAdmin) {
      loadSlots();
    }
  }, [isAdmin]);
  
  // Lade existierende Konfiguration
  useEffect(() => {
    const awsConfig = segment.config?.searchapp_redirects || {};
    if (awsConfig.enabled && awsConfig.slot) {
      setSelectedSlot(awsConfig.slot);
      setSelectedDomains(awsConfig.domains || []);
      setUseAllDomains(awsConfig.domains?.length === awsConfig.slot?.domains?.length);
      setLinksPerMode(awsConfig.links_per_mode || 'per_segment');
      setUseHybridMode(awsConfig.use_hybrid_mode || false);
      setHybridRegion(
        EXCLUDED_HYBRID_REGIONS.has(awsConfig.hybrid_region)
          ? DEFAULT_HYBRID_REGION
          : (awsConfig.hybrid_region || DEFAULT_HYBRID_REGION)
      );
      setLinkStyle(awsConfig.link_style || 'SHORT');
      setEnabledLinkTypes(awsConfig.enabled_link_types || { logo: true, cta: true, unsubscribe: true });
    }
  }, [segment]);
  
  const loadSlots = async () => {
    setLoading(true);
    console.log('Loading Search.app redirect slots...');
    try {
      const response = await axios.get('/api/searchapp-redirect/slots');
      console.log('Slots response:', response.data);
      const loadedSlots = response.data.data?.slots || response.data.slots || [];
      setSlots(loadedSlots);
      console.log('Loaded slots:', loadedSlots);
      if (loadedSlots.length > 0) {
        toast.success(`✅ ${loadedSlots.length} Redirect-Slots geladen`);
      }
    } catch (error) {
      console.error('Failed to load redirect slots:', error);
      toast.error('Fehler beim Laden der Redirect-Slots: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSlotChange = (slotIndex) => {
    const slot = slots[slotIndex];
    setSelectedSlot(slot);
    setSelectedDomains(slot.domains); // Default: alle
    setUseAllDomains(true);
  };
  
  const toggleDomain = (domain) => {
    if (selectedDomains.includes(domain)) {
      setSelectedDomains(selectedDomains.filter(d => d !== domain));
    } else {
      setSelectedDomains([...selectedDomains, domain]);
    }
    setUseAllDomains(false);
  };
  
  const handleSave = () => {
    // Custom-Domains parsen
    let finalDomains = [];
    if (useCustomDomains) {
      finalDomains = customDomainsText
        .split('\n')
        .map(d => d.trim().toLowerCase())
        .filter(d => d && d.includes('.'));
      
      if (finalDomains.length === 0) {
        toast.error('Bitte geben Sie mindestens eine Domain ein');
        return;
      }
    } else {
      if (!selectedSlot) {
        toast.error('Bitte wählen Sie einen Slot oder aktivieren Sie Custom-Domains');
        return;
      }
      finalDomains = useAllDomains ? selectedSlot.domains : selectedDomains;
    }
    
    const config = {
      enabled: true,
      use_custom_domains: useCustomDomains,
      slot: useCustomDomains ? null : selectedSlot,
      domains: finalDomains,
      links_per_mode: linksPerMode,
      use_hybrid_mode: useHybridMode,
      hybrid_region: useHybridMode ? hybridRegion : null,
      link_style: linkStyle,
      enabled_link_types: enabledLinkTypes,
      links: null  // Wird später beim Campaign-Start generiert
    };
    
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.searchapp_redirects = config;
    
    onUpdate(updated);
    toast.success('Search.app-Redirect Konfiguration gespeichert');
  };
  
  const handleDisable = () => {
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.searchapp_redirects = { enabled: false };
    
    onUpdate(updated);
    toast.success('Search.app-Redirects deaktiviert');
  };
  
  // Debug
  console.log('SearchAppRedirectConfig rendered:', { isAdmin, segment: segment?.provider_name });
  
  if (!isAdmin) {
    console.log('SearchAppRedirectConfig: Not admin, hiding');
    return null; // Nur für Admins sichtbar
  }
  
  const awsConfig = segment.config?.searchapp_redirects || {};
  const isEnabled = awsConfig.enabled;
  
  console.log('SearchAppRedirectConfig: Showing UI', { isEnabled, slotsCount: slots.length });
  
  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border-2 border-blue-300 dark:border-blue-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Link className="h-5 w-5 mr-2 text-blue-600" />
          🔗 Search.app Redirects (Admin)
        </h3>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={isEnabled || false}
            onChange={(e) => {
              if (e.target.checked) {
                // Aktivieren - zeige UI
                const updated = { ...segment };
                if (!updated.config) updated.config = {};
                if (!updated.config.searchapp_redirects) updated.config.searchapp_redirects = {};
                updated.config.searchapp_redirects.enabled = true;
                onUpdate(updated);
              } else {
                // Deaktivieren
                handleDisable();
              }
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-200">Aktiviert</span>
        </div>
      </div>
      
      {(isEnabled || isEnabled === undefined) && (
        <div className="space-y-4">
          {/* Custom-Domains Toggle */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomDomains}
                onChange={(e) => {
                  setUseCustomDomains(e.target.checked);
                  if (e.target.checked) {
                    setSelectedSlot(null);
                  }
                }}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                🌐 Custom-Domains (beliebige Domains eingeben)
              </span>
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Für Admin: Eigene Domains statt Slot-Domains verwenden
            </div>
          </div>
          
          {useCustomDomains ? (
            /* Custom-Domain-Eingabe */
            <div className="border border-blue-300 dark:border-blue-700 p-4 rounded-md bg-blue-50 dark:bg-blue-900/20">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                🌐 Custom-Domains (eine pro Zeile):
              </label>
              <textarea
                value={customDomainsText}
                onChange={(e) => setCustomDomainsText(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 font-mono"
                placeholder="example.com&#10;another-domain.de&#10;my-site.net&#10;redirect-domain.com"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                ✅ {customDomainsText.split('\n').filter(d => d.trim() && d.includes('.')).length} Domains erkannt
              </div>
            </div>
          ) : (
            /* Slot-Auswahl */
            <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Redirect-Slot wählen
            </label>
            {loading ? (
              <div className="text-sm text-gray-500">Lade Slots...</div>
            ) : slots.length > 0 ? (
              <select
                value={selectedSlot ? slots.indexOf(selectedSlot) : ''}
                onChange={(e) => handleSlotChange(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper bg-white dark:bg-dark-card text-gray-900 dark:text-gray-100 rounded-md"
              >
                <option value="">-- Slot wählen --</option>
                {slots.map((slot, idx) => (
                  <option key={idx} value={idx}>
                    {slot.type === 'landing' ? '🌐' : '🔀'} {slot.target_url} ({slot.type === 'landing' ? `Landing · ${slot.total_domains} Domains` : `${slot.total_domains} Domains`})
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-sm text-red-600">Keine Slots verfügbar</div>
            )}
          </div>
          
          </>
          )}
          
          {/* Domain-Auswahl */}
          {!useCustomDomains && selectedSlot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Domains nutzen
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useAllDomains}
                    onChange={(e) => {
                      setUseAllDomains(e.target.checked);
                      if (e.target.checked) setSelectedDomains(selectedSlot.domains);
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    ✅ Alle {selectedSlot.domains.length} Domains nutzen
                  </span>
                </label>
                
                {!useAllDomains && (
                  <div className="ml-6 space-y-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-dark-copper rounded p-2">
                    {selectedSlot.domains.map((domain, idx) => (
                      <label key={idx} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedDomains.includes(domain)}
                          onChange={() => toggleDomain(domain)}
                          className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-xs text-gray-700 dark:text-gray-200">{domain}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedDomains.length} Domain(s) ausgewählt
                </p>
              </div>
            </div>
          )}
          
          {/* Search.app Region */}
          {/* Link-Typen auswählen */}
          {selectedSlot && (
            <div className="border border-gray-300 dark:border-dark-copper p-3 rounded-md">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                🔗 Link-Typen generieren:
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledLinkTypes.logo}
                    onChange={(e) => setEnabledLinkTypes({...enabledLinkTypes, logo: e.target.checked})}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">📸 Logo Links (für {'{{SEARCHLOGO}}'})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledLinkTypes.cta}
                    onChange={(e) => setEnabledLinkTypes({...enabledLinkTypes, cta: e.target.checked})}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">🎯 CTA Links (für {'{{SEARCHCTA}}'})</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabledLinkTypes.unsubscribe}
                    onChange={(e) => setEnabledLinkTypes({...enabledLinkTypes, unsubscribe: e.target.checked})}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">📧 Unsubscribe Links (für {'{{SEARCHUNSUB}}'})</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Hybrid-Modus (search.app → S3) */}
          {(selectedSlot || useCustomDomains) && (
            <div className="border border-gray-300 dark:border-dark-copper p-3 rounded-md">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useHybridMode}
                  onChange={(e) => setUseHybridMode(e.target.checked)}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  🔗 Hybrid-Modus (search.app → S3)
                </span>
              </label>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                Kurze search.app Links die auf S3-Buckets redirecten
              </div>
              
              {useHybridMode && (
                <div className="mt-2 ml-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    S3 Region:
                  </label>
                  <select
                    value={hybridRegion}
                    onChange={(e) => setHybridRegion(e.target.value)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800"
                  >
                    <option value="ireland">Ireland (eu-west-1)</option>
                  </select>
                </div>
              )}
            </div>
          )}
          
          {/* Link-Style Auswahl */}
          {(selectedSlot || useCustomDomains) && (
            <div className="border border-gray-300 dark:border-dark-copper p-3 rounded-md">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                🔗 Link-Style:
              </label>
              <select
                value={linkStyle}
                onChange={(e) => setLinkStyle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
              >
                <option value="SHORT">SHORT - Kurze Links (17 Zeichen)</option>
                <option value="UNGUESSABLE">UNGUESSABLE - Lange, sichere Links</option>
              </select>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                SHORT: https://search.app/ABC123xyz (Kurz)<br/>
                UNGUESSABLE: https://search.app/kHri4ZYTnoTQpppy8a3f5 (Lang)
              </div>
            </div>
          )}
          
          {/* Links pro Segment/Slot */}
          {(selectedSlot || useCustomDomains) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Link-Erstellung
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="per_segment"
                    checked={linksPerMode === 'per_segment'}
                    onChange={(e) => setLinksPerMode(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    📦 Pro Segment (ein Link-Set für alle Slots)
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="per_slot"
                    checked={linksPerMode === 'per_slot'}
                    onChange={(e) => setLinksPerMode(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-200">
                    🎯 Pro Slot (eigenes Link-Set pro Slot)
                  </span>
                </label>
              </div>
            </div>
          )}
          
          {/* Info Box */}
          {selectedSlot && (
            <div className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded text-xs text-blue-800 dark:text-blue-200">
              <div className="font-semibold mb-1">ℹ️ Search.app-Redirect Placeholders:</div>
              <div className="space-y-1 font-mono">
                <div>• <strong>{'{{Search.appLOGO}}'}</strong> - Tracking Pixel (S3 → {selectedSlot.target_url}/logo)</div>
                <div>• <strong>{'{{Search.appCTA}}'}</strong> - Call-to-Action (S3 → {selectedSlot.target_url})</div>
                <div>• <strong>{'{{Search.appUNSUBSCRIBE}}'}</strong> - Unsubscribe (S3 → {selectedSlot.target_url}/unsubscribe)</div>
              </div>
              <div className="mt-2 text-xs">
                Links werden beim Campaign-Start automatisch generiert und auf {selectedDomains.length} Domain(s) verteilt.
              </div>
            </div>
          )}
          
          {/* Save Button */}
          {((selectedSlot && selectedDomains.length > 0) || (useCustomDomains && customDomainsText.trim())) && (
            <button
              onClick={handleSave}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Search.app-Redirect Konfiguration speichern</span>
            </button>
          )}
        </div>
      )}
      
      {/* Status Anzeige */}
      {isEnabled && awsConfig.slot && (
        <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs text-green-800 dark:text-green-200">
          ✅ Konfiguriert: {awsConfig.slot.target_url} mit {awsConfig.domains?.length || 0} Domain(s)
        </div>
      )}
    </div>
  );
};

export default SearchAppRedirectConfig;

